// project_management#575 — list values in the admin filter panel.
//
// Admins paste lists from scripts and spreadsheets: comma-, space-, tab- or
// newline-separated. The backend splits identifier filters (ids, file names,
// user ids, e-mails, statuses, flags) on any run of commas, semicolons or
// whitespace and keeps `comment` comma-only free text; these helpers apply the
// same rules in the browser.
//
// A single-line <input> never keeps the line breaks of a pasted value, and
// what it does instead depends on the browser (spaces, nothing — gluing the
// values into one token — or only the first line); `insertPastedList` turns
// the line breaks and tabs into ", " itself before the text is inserted.

// A run of commas, semicolons and/or whitespace (`\s` covers \n, \r, \t, NBSP).
const IDENTIFIER_SEPARATORS = /[\s,;]+/;

const LINE_BREAKS_OR_TABS = /[\r\n\t]/;

const SEPARATOR = ', ';

/** Identifier list: split on runs of commas, semicolons or whitespace. */
export function splitIdentifierList(value: string | null | undefined): string[] {
  if (!value) return [];

  return value.split(IDENTIFIER_SEPARATORS).filter(Boolean);
}

/** Does this pasted text need normalising (a column, a spreadsheet row)? */
export function isMultiValuePaste(text: string): boolean {
  return LINE_BREAKS_OR_TABS.test(text);
}

/**
 * Pasted text → one line: every line/cell becomes a value separated by ", ".
 * Blank lines and separators already at a line's ends are dropped, so
 * "a,\nb\n\n" becomes "a, b". Spaces inside a line are kept (a comment line).
 */
export function normalizePastedList(text: string): string {
  return text
    .split(/[\r\n\t]+/)
    .map((piece) => piece.replace(/^[\s,;]+|[\s,;]+$/g, ''))
    .filter(Boolean)
    .join(SEPARATOR);
}

/**
 * The input's value after pasting `pasted` over the selection
 * [`start`, `end`), plus the caret position right after the inserted text.
 * Adds a separator where the pasted values meet existing text.
 */
export function insertPastedList(
  current: string,
  start: number,
  end: number,
  pasted: string,
): { value: string; caret: number } {
  const before = current.slice(0, start);
  const after = current.slice(end);
  const normalized = normalizePastedList(pasted);

  if (!normalized) return { value: before + after, caret: before.length };

  const lead = before.trim() && !/[\s,;]$/.test(before) ? SEPARATOR : '';
  const trail = after.trim() && !/^[\s,;]/.test(after) ? SEPARATOR : '';
  const inserted = `${lead}${normalized}${trail}`;

  return { value: before + inserted + after, caret: before.length + inserted.length };
}
